import Bugsnag from '@bugsnag/js';
import { Box, CarouselSlide } from '@vp/swan';
import { __image } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useRecommendationsQuery } from '../../graphql/recommendations.generated';
import { buildCloudinaryURL, isPlacementLocaleSupported } from '../../lib';
import { getRecommendationTrackingData } from '../../lib/getRecommendationTrackingData';
import { RecommendationSkeletonTile, RecommendationTile } from '../RecommendationTile';
import { RecommendationsCarousel } from '../RecommendationsCarousel';
import { RecommendationsHeader } from '../RecommendationsHeader';
import { RecommendationsLayout } from '../RecommendationsLayout';
import type { Placement } from '../../types';

const MIN_RECOMMENDATIONS = 6;

interface RecommendationsProps {
    placement?: Placement;
    onError?: (error: string) => void;
}

export function Recommendations({
    placement = 'recently-viewed',
    onError,
}: RecommendationsProps = {}) {
    const { locale, pageData } = usePage();

    if (!isPlacementLocaleSupported(placement, locale)) {
        const formattedError = `Recommendations placement "${placement}" is not supported for locale "${locale}". Please check placementConfigs.`;
        console.warn(formattedError);
        onError?.(formattedError);
        return null;
    }

    const { data, loading, error } = useRecommendationsQuery({
        variables: {
            placement,
        },
    });

    if (loading) {
        return (
            <RecommendationsLayout>
                <RecommendationsHeader />
                <Box data-testid="carousel-wrapper" aria-live="polite" aria-busy="true">
                    <RecommendationsCarousel>
                        {Array.from({ length: MIN_RECOMMENDATIONS }).map((_, index) => (
                            <CarouselSlide key={index}>
                                <RecommendationSkeletonTile />
                            </CarouselSlide>
                        ))}
                    </RecommendationsCarousel>
                </Box>
            </RecommendationsLayout>
        );
    }

    if (error || !data || !data?.recommendations) {
        const formattedError = `Error fetching recommendations: ${
            error ? error.message : 'No data returned'
        }`;
        Bugsnag.notify(new Error(formattedError));
        onError?.(formattedError);
        return null;
    }

    return (
        <RecommendationsLayout>
            <RecommendationsHeader />
            <Box data-testid="carousel-wrapper" aria-live="polite" aria-busy="false">
                <RecommendationsCarousel>
                    {data.recommendations.map((recommendation, index) => {
                        return (
                            <CarouselSlide key={index} data-testid="product-carousel-slide">
                                <RecommendationTile
                                    key={recommendation.mpvID}
                                    title={recommendation.name}
                                    imageSrc={buildCloudinaryURL(
                                        __image(`PreviewImages/${recommendation.mpvID}`)
                                    )}
                                    fallbackImageSrc={recommendation.fallbackImageURL || ''}
                                    ctaLink={`/design/get-started?mpvId=${recommendation.mpvID}&locale=${locale}`}
                                    price={recommendation.price ?? undefined}
                                    trackingData={getRecommendationTrackingData(recommendation, {
                                        pageName: pageData.pageName,
                                        pageStage: pageData.pageStage,
                                        pageSection: pageData.pageSection,
                                        pagePath: pageData.pagePath ?? window.location.pathname,
                                        position: index,
                                    })}
                                />
                            </CarouselSlide>
                        );
                    })}
                </RecommendationsCarousel>
            </Box>
        </RecommendationsLayout>
    );
}
